// src/context/BlogContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const BlogContext = createContext();

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
};

export const BlogProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // 获取文章数据
  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/posts'); // 替换为您的实际API地址
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('获取文章失败:', error);
      // 如果API请求失败，可以降级到本地存储
      const storedPosts = JSON.parse(localStorage.getItem('posts')) || [];
      setPosts(storedPosts);
    } finally {
      setLoading(false);
    }
  };

  // 获取用户数据
  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('获取用户失败:', error);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchPosts();
    fetchUsers();
    
    // 监听数据变化，自动保存到localStorage
    const handleStorageChange = (event) => {
      if (event.key === 'posts') {
        setPosts(JSON.parse(event.newValue));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 其他方法保持不变...
  const addPost = (postData) => {
    // 发送POST请求到服务器
    const newPost = {
      ...postData,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      views: 0,
      likes: 0
    };
    
    // 这里应该发送请求到后端API
    // fetch('/api/posts', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(newPost)
    // })
    // .then(response => response.json())
    // .then(savedPost => {
    //   setPosts(prev => [savedPost, ...prev]);
    //   localStorage.setItem('posts', JSON.stringify([savedPost, ...posts]));
    // });
    
    // 临时本地保存
    const updatedPosts = [newPost, ...posts];
    setPosts(updatedPosts);
    localStorage.setItem('posts', JSON.stringify(updatedPosts));
    return newPost;
  };

  const updatePost = (id, postData) => {
    // 发送PUT请求到服务器
    // 类似addPost的实现
    const updatedPosts = posts.map(post => 
      post.id === id 
        ? { ...post, ...postData, updatedAt: new Date().toISOString() }
        : post
    );
    setPosts(updatedPosts);
    localStorage.setItem('posts', JSON.stringify(updatedPosts));
  };

  const deletePost = (id) => {
    // 发送DELETE请求到服务器
    // 类似addPost的实现
    const updatedPosts = posts.filter(post => post.id !== id);
    setPosts(updatedPosts);
    localStorage.setItem('posts', JSON.stringify(updatedPosts));
  };

  const value = {
    posts,
    users,
    categories,
    tags,
    loading,
    addPost,
    updatePost,
    deletePost
  };

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
};